import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Layers, ShieldCheck, Wrench } from "lucide-react";
import { FORMAT_HUBS } from "../content/formats";
import { PROVIDER_LIMITS } from "../content/limits";
import { ARTICLE_HUBS } from "../content/personas";
import { buildLangPath, useUi } from "../i18n";

const LOCALIZED_HUB_SLUGS = new Set([
  "/",
  "/send-mp4",
  "/wetransfer-alternative",
  "/sendgb-alternative",
  "/request-files",
  "/video-review-tool",
  "/gmail-attachment-size-limit",
  "/from-iphone-to-pc",
  "/send-over-2-gb",
]);

export function smartLangPath(path: string, lang: string): string {
  if (lang === "en") return path;
  if (path === "/") return buildLangPath("/", lang as "ru");
  if (["ru", "de", "tr", "es", "fr"].includes(lang) && LOCALIZED_HUB_SLUGS.has(path)) {
    return buildLangPath(path, lang as "ru");
  }
  return buildLangPath("/", lang as "ru");
}

/**
 * Semantic Contextual Cross-Linking Silo Block.
 * Connects Formats <-> Personas/Solutions <-> Platform Limits <-> Interactive Tools by logical intent.
 */
export function ContextualLinks({ currentSlug }: { currentSlug: string }) {
  const { lang } = useUi();

  // Pick logically related links based on the current page's topic
  const isVideo = /mp4|mov|4k|video|tiktok|instagram|videographer|youtube/i.test(currentSlug);
  const isDesignOrPhoto = /psd|raw|illustrator|3d|photo|architect|cad|dwg/i.test(currentSlug);
  const isLegalOrBiz = /pdf|lawyer|accountant|healthcare|sql|database|backup|zip|iso|receipt|encrypt/i.test(currentSlug);

  const recommendedTools = isVideo
    ? [
        { to: "/video-review-tool", label: "Frame-Accurate Video Review Player (Free Frame.io Alternative)" },
        { to: "/request-files", label: "Receive Raw Footage from Clients (Reverse Drop Portal)" },
        { to: "/tools/upload-time-calculator", label: "4K Video Upload Time Calculator" },
        { to: "/sell-digital-files", label: "Sell Video Cuts with Pay-to-Decrypt Links" },
      ]
    : isDesignOrPhoto
      ? [
          { to: "/request-files", label: "Client File Request Portal (Collect RAW & CAD Files)" },
          { to: "/sell-digital-files", label: "Sell Digital Designs & 3D Models via Escrow Link" },
          { to: "/drop", label: "LimeDrop Direct P2P (Studio Workstation Transfer)" },
          { to: "/verify-receipt", label: "Certified Destruction Verifier (NDA Protection)" },
        ]
      : [
          { to: "/verify-receipt", label: "Certified Destruction Verifier (GDPR & Legal Proof)" },
          { to: "/tools/encrypt-file", label: "Offline File Encryptor (AES-256-GCM + Argon2id)" },
          { to: "/tools/file-checksum", label: "SHA-256 Cryptographic Checksum Verifier" },
          { to: "/request-files", label: "Receive Confidential Client Documents (No Login)" },
          { to: "/developers", label: "Developers & Terminal CLI (transfer.sh Alternative)" },
        ];

  const relatedSolutions = isVideo
    ? [
        { to: "/for-videographers", label: "For Videographers & Film Editors" },
        { to: "/instagram-original-video", label: "Send Uncompressed Video on Instagram" },
        { to: "/share-original-tiktok-videos", label: "Share Original TikTok Source Videos" },
        { to: "/send-over-2-gb", label: "Send Files Over 2 GB Free" },
        { to: "/send-1-tb-files", label: "Send 500 GB & 1 TB Cinema Archives" },
      ]
    : isDesignOrPhoto
      ? [
          { to: "/for-photographers", label: "For Photographers (Full-Res RAW Delivery)" },
          { to: "/for-architects", label: "For Architects & BIM Engineering" },
          { to: "/for-game-developers", label: "For Game Studios & 3D Artists" },
          { to: "/send-folders-without-zipping", label: "Send Project Folders Without Zipping" },
        ]
      : [
          { to: "/for-lawyers", label: "For Law Firms & Legal Discovery" },
          { to: "/for-accountants", label: "For Accountants & Tax Audits" },
          { to: "/for-healthcare", label: "For Healthcare & DICOM Medical Scans" },
          { to: "/share-database-backups", label: "Share SQL & Database Backups Securely" },
          { to: "/send-files-anonymously", label: "How to Send Files Anonymously" },
          { to: "/encrypted-file-transfer", label: "TLS vs At-Rest vs End-to-End Encryption" },
        ];

  return (
    <aside style={{ marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
        {/* 1. Related Interactive Tools & Workflows */}
        <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 14 }}>
          <b style={{ fontSize: 13, color: "var(--lime)", display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <Wrench size={14} /> Recommended Tools &amp; Workflows
          </b>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            {recommendedTools.map((item) => (
              <li key={item.to}>
                <Link to={smartLangPath(item.to, lang)}>{item.label} →</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* 2. Related Industry Solutions & Guides */}
        <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 14 }}>
          <b style={{ fontSize: 13, color: "var(--lime)", display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <Compass size={14} /> Related Solutions &amp; Guides
          </b>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            {relatedSolutions.map((item) => (
              <li key={item.to}>
                <Link to={smartLangPath(item.to, lang)}>{item.label} →</Link>
              </li>
            ))}
            <li>
              <Link to="/ultimate-file-transfer-guide">Ultimate 2026 Large File Transfer Guide →</Link>
            </li>
            <li>
              <Link to="/research">LSC1 Academic Working Paper &amp; Benchmark Dataset →</Link>
            </li>
            <li>
              <Link to="/citation">Cite LSC1 Protocol (BibTeX / APA / IEEE) →</Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Complete Silo Directory Strip */}
      <div style={{ marginTop: 20, fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
        <div>
          <b style={{ color: "var(--text)" }}>All File Formats: </b>
          {FORMAT_HUBS.map((f, i) => (
            <React.Fragment key={f.slug}>
              {i > 0 && " · "}
              <Link to={smartLangPath(`/${f.slug}`, lang)} style={{ color: "var(--muted)" }}>{f.formatName}</Link>
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 4 }}>
          <b style={{ color: "var(--text)" }}>Platform Attachment Limits: </b>
          <Link to="/email-attachment-size-limits" style={{ color: "var(--muted)" }}>All Email Limits</Link>
          {PROVIDER_LIMITS.map((p) => (
            <React.Fragment key={p.slug}>
              {" · "}
              <Link to={smartLangPath(`/${p.slug}`, lang)} style={{ color: "var(--muted)" }}>{p.provider}</Link>
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 4 }}>
          <b style={{ color: "var(--text)" }}>Comparisons &amp; Sizes: </b>
          {ARTICLE_HUBS.map((a, i) => (
            <React.Fragment key={a.slug}>
              {i > 0 && " · "}
              <Link to={smartLangPath(`/${a.slug}`, lang)} style={{ color: "var(--muted)" }}>{a.slug.replace(/-/g, " ")}</Link>
            </React.Fragment>
          ))}
        </div>
      </div>
    </aside>
  );
}
